import { PocList } from 'commons/components/components-wrapper/poc-list.component';
import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { getTokenFromStorage, routeIsAllowed } from 'core/utils/auth-provider-config';
import { useState } from 'react';
import { DevInfoLabel, DevInfoMenuFooter, SideMenuList } from './app.menu.style';

import { offset } from '@formkit/tempo';
import packageJson from '@root/package.json';
import { useAuthContext } from 'core/auth/hooks/use-auth-context.hook';
import FeatureFlagGuard from 'core/auth/feature-flag-guard.component';
import { env } from 'core/constants/env.constants';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { handleOnClickTenantLabel } from './app.menu.business';
import { useAppMenuHandle } from './use-app-menu-handle.hook';
import { DevicesMenu } from './components/devices-menu.components';
import { LotsMenu } from './components/lots-menu.components';
import { OperatorsMenu } from './components/operators-menu.components';
import { TooltipTokenCopiedComponent } from './components/tooltip-token-copied.component';
import { TrainingsMenu } from './components/trainings-menu.components';
import {
    DEVICE_MANAGEMENT_FEATURE_FLAG,
    MATERIAL_MANAGEMENT_FEATURE_FLAG,
} from 'core/service/feature-flag/feature-flag.constants';

export const SideMenuComponent: React.FC = () => {
    const { navigationClick, selected, setSelected } = useAppMenuHandle();

    const [operatorsMenuVisible, setOperatorsMenuVisible] = useState<boolean>(true);
    const [devicesMenuVisible, setDevicesMenuVisible] = useState<boolean>(true);
    const [lotsMenuVisible, setLotsMenuVisible] = useState<boolean>(true);
    const [isVisibleTenantTooltip, setIsVisibleTenantTooltip] = useState<boolean>(false);
    const [isVisibleTokenTooltip, setIsVisibleTokenTooltip] = useState<boolean>(false);
    const { userRole } = useAuthContext();

    const storageToken = getTokenFromStorage();

    const { appSettings } = useUserPreferencesContext();

    const showTenantTooltip = (ev: React.MouseEvent<HTMLSpanElement>) => {
        const node = ev.currentTarget;
        const targetParentNode = ev.currentTarget.parentNode as HTMLElement;
        if (node.scrollWidth > targetParentNode.scrollWidth) {
            setIsVisibleTenantTooltip(true);
        }
    };

    return (
        <SideMenuList>
            <div>
                <PocList
                    variant="side-menu"
                    size="small"
                    noBorder
                    onSelectedChange={(ev) => {
                        ev.stopPropagation();
                        const newSelection = (ev.detail[0] as string) ?? 'operators';
                        if (newSelection !== selected) {
                            setSelected(newSelection);
                            navigationClick(newSelection);
                        }
                    }}
                    selected={[selected]}
                    style={{ flex: '1' }}
                >
                    {routeIsAllowed(userRole, '/operators') ? (
                        <OperatorsMenu
                            selected={selected}
                            menuVisible={operatorsMenuVisible}
                            onNavigationClick={navigationClick}
                            onExpandedChange={setOperatorsMenuVisible}
                        />
                    ) : undefined}
                    {routeIsAllowed(userRole, '/trainings') ? (
                        <TrainingsMenu onNavigationClick={navigationClick} />
                    ) : undefined}
                    {routeIsAllowed(userRole, '/lots') ? (
                        <FeatureFlagGuard featureFlag={MATERIAL_MANAGEMENT_FEATURE_FLAG}>
                            <LotsMenu
                                selected={selected}
                                menuVisible={lotsMenuVisible}
                                onNavigationClick={navigationClick}
                                onExpandedChange={setLotsMenuVisible}
                            />
                        </FeatureFlagGuard>
                    ) : undefined}
                    {routeIsAllowed(userRole, '/devices') ? (
                        <FeatureFlagGuard featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}>
                            <DevicesMenu
                                selected={selected}
                                menuVisible={devicesMenuVisible}
                                onNavigationClick={navigationClick}
                                onExpandedChange={setDevicesMenuVisible}
                            />
                        </FeatureFlagGuard>
                    ) : undefined}
                </PocList>
            </div>
            {env.VITE_ALLOW_DEV_OPTIONS === true && (
                <DevInfoMenuFooter>
                    <div className="footer-container">
                        <DevInfoLabel>
                            <PocTypography element={'span'} variant={'caption'} className="info-label">
                                {`${appSettings.tenantConfig.zoneId} (${offset(new Date(), appSettings.tenantConfig.zoneId)})`}
                            </PocTypography>
                        </DevInfoLabel>
                        {storageToken ? (
                            <DevInfoLabel
                                onClick={(ev) => {
                                    handleOnClickTenantLabel(ev, setIsVisibleTokenTooltip);
                                }}
                            >
                                <TooltipTokenCopiedComponent visible={isVisibleTokenTooltip} />

                                <PocTypography
                                    id="tenant-label"
                                    element={'span'}
                                    variant={'caption'}
                                    className="info-label"
                                    onMouseEnter={showTenantTooltip}
                                    onMouseLeave={() => setIsVisibleTenantTooltip(false)}
                                >
                                    {storageToken.profile.tenant_alias}
                                </PocTypography>
                                <PocTooltip
                                    anchor={'tenant-label'}
                                    strategy="fixed"
                                    placement="top"
                                    disableTriggers
                                    visible={isVisibleTenantTooltip}
                                >
                                    {storageToken.profile.tenant_alias}
                                </PocTooltip>
                            </DevInfoLabel>
                        ) : undefined}
                        {packageJson ? (
                            <DevInfoLabel>
                                <PocTypography element={'span'} variant={'caption'} className="info-label">
                                    v{env.APP_VERSION}
                                </PocTypography>
                            </DevInfoLabel>
                        ) : undefined}
                    </div>
                </DevInfoMenuFooter>
            )}
        </SideMenuList>
    );
};
